import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FilterAlt } from '@mui/icons-material';
import { Operator, UrlFilters } from '../../hooks/useUrlFilters';
import DropDown, { DropDownOption } from '../DropDown';
import ColumnDateRangeFiltersMenu from './ColumnDateRangeFiltersMenu';
import { ColumnType } from './types';

type Props = {
  columnId: string;
  isLoading?: boolean;
  isCounterHidden?: boolean;
  getFilterOptions: (
    columnId: string,
    isCounterHidden: boolean,
  ) => DropDownOption[];
  selectedFilters?: UrlFilters;
  showSelectAll?: boolean;
  addFilter: (
    columnId: string,
    relationship: Operator,
    value: string[],
  ) => void;
  updateFilter: (
    property: string,
    operator: Operator,
    value: string[],
    newValue: string[],
  ) => void;
  renderer?: (value: string) => string;
  emptyValueLabel?: string;
  type?: ColumnType;
  sortOptions?: boolean;
  width?: number;
};

const MenuButton = styled(FilterAlt)<{
  $selected?: boolean;
}>`
  width: 14px;
  max-width: 14px;
  cursor: pointer;
  color: ${({ theme, $selected }) =>
    $selected ? theme.palette.common.blue : theme.palette.common.gray2};
  &:hover {
    color: ${({ theme, $selected }) =>
      $selected ? theme.palette.common.blue5 : theme.palette.common.black};
  }
`;

const ColumnFiltersMenu = ({
  isLoading,
  columnId,
  isCounterHidden,
  getFilterOptions,
  selectedFilters,
  addFilter,
  updateFilter,
  renderer,
  emptyValueLabel,
  showSelectAll,
  type = ColumnType.Text,
  sortOptions,
  width,
}: Props) => {
  const [options, setOptions] = useState<DropDownOption[]>([]);

  const currentColumnSelectedFilters = useMemo(
    () =>
      selectedFilters?.find(
        (filter) =>
          filter.property === columnId && filter.operator === 'equals',
      ),
    [columnId, selectedFilters],
  );

  const handleMenuOpen = useCallback(() => {
    setOptions(getFilterOptions(columnId, !!isCounterHidden));
  }, [columnId, isCounterHidden, getFilterOptions]);

  const handleChange = useCallback(
    (selectedValues: string[]) => {
      if (currentColumnSelectedFilters) {
        updateFilter(
          columnId,
          'equals',
          currentColumnSelectedFilters.value,
          selectedValues,
        );
      } else {
        addFilter(columnId, 'equals', selectedValues);
      }
    },
    [addFilter, columnId, currentColumnSelectedFilters, updateFilter],
  );

  if (type === ColumnType.Date) {
    return (
      <ColumnDateRangeFiltersMenu
        emptyValueLabel={emptyValueLabel}
        menuButton={<MenuButton />}
        menuSelectedButton={<MenuButton $selected />}
        value={currentColumnSelectedFilters?.value || []}
        onOpen={handleMenuOpen}
        onChange={(val) => handleChange(val as string[])}
      />
    );
  }

  return (
    <DropDown
      menuButton={<MenuButton />}
      menuSelectedButton={<MenuButton $selected />}
      options={options}
      value={currentColumnSelectedFilters?.value || []}
      onOpen={handleMenuOpen}
      onChange={(val) => handleChange(val as string[])}
      optionRenderer={renderer}
      emptyValueLabel={emptyValueLabel}
      showSelectAll={showSelectAll}
      sortOptions={sortOptions}
      multiple
      showSearch
      showClear
      showCounter={!isCounterHidden}
      isLoading={isLoading}
      menuWidth={width ? `${width}px` : undefined}
    />
  );
};

export default ColumnFiltersMenu;
